import { useEffect, useMemo, useState } from 'react'
import type { WorkspaceActivityRecord } from '../../../../services/activityService'
import {
  subscribeAuditExportJobs,
  type AuditExportJob,
} from '../../../../services/auditExportService'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type {
  LiveModuleHeadline,
  LiveModuleRecord,
  LiveModuleSparkline,
} from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeToDateRange } from './bucketize'

export type UseExportActivityModuleParams = {
  workspaceId: string
  timeRange: AnalysisTimeRangeKey
  auditRecords: WorkspaceActivityRecord[]
  loading?: boolean
  error?: Error | null
  /** Bump to force a re-subscribe of the export jobs listener. */
  refreshKey?: number
  now?: Date
}

const isExportRecord = (r: WorkspaceActivityRecord) => {
  const type = (r.activityType || '').toUpperCase()
  const act = (r.action || '').toUpperCase()
  return type.includes('EXPORT') || act.includes('EXPORT')
}

export const useExportActivityModule = ({
  workspaceId,
  timeRange,
  auditRecords,
  loading: auditLoading = false,
  error: auditError = null,
  refreshKey = 0,
  now,
}: UseExportActivityModuleParams) => {
  const [jobs, setJobs] = useState<AuditExportJob[]>([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [jobsError, setJobsError] = useState<Error | null>(null)

  useEffect(() => {
    if (!workspaceId) {
      setJobs([])
      setJobsLoading(false)
      return undefined
    }

    setJobsLoading(true)
    const unsubscribe = subscribeAuditExportJobs(
      workspaceId,
      (loadedJobs) => {
        setJobs(loadedJobs)
        setJobsLoading(false)
      },
      (err) => {
        setJobsError(err)
        setJobs([])
        setJobsLoading(false)
      },
    )

    return () => unsubscribe()
  }, [workspaceId, refreshKey])

  return useMemo(() => {
    const { from, to } = timeRangeToDateRange(timeRange, now)
    const ledgerExports = auditRecords.filter(isExportRecord)

    // Filter jobs within time window
    const windowJobs = jobs.filter((job) => {
      const created = job.createdAt
      const millis =
        created instanceof Date
          ? created.getTime()
          : typeof created === 'number'
            ? created
            : created && typeof created === 'object' && 'toMillis' in created && typeof (created as { toMillis: () => number }).toMillis === 'function'
              ? (created as { toMillis: () => number }).toMillis()
              : 0
      return millis >= from.getTime() && millis <= to.getTime()
    })

    // Combine timestamps
    const timestamps: Date[] = [
      ...ledgerExports.map((r) => r.createdAt as Date).filter(Boolean),
      ...windowJobs.map((j) => {
        const c = j.createdAt
        if (c instanceof Date) return c
        if (typeof c === 'number') return new Date(c)
        if (c && typeof c === 'object' && 'toDate' in c && typeof (c as { toDate: () => Date }).toDate === 'function') {
          return (c as { toDate: () => Date }).toDate()
        }
        return new Date()
      }),
    ]

    const points = bucketize(timestamps, timeRange, now)
    const delta = calculateDelta(points)

    // Dedup: a single export can appear both as an AuditExportJob and as a
    // ledger `EXPORT`/`EXPORT_RAW_CSV` row. When the ledger row's targetId
    // matches the job id (Firestore doc id), collapse them so we never
    // double-count or render two rows for the same event.
    const jobIds = new Set(windowJobs.map((job) => job.id))
    const uniqueLedgerExports = ledgerExports.filter((r) => {
      const targetId = (r as { targetId?: string; nodeId?: string }).targetId ?? (r as { nodeId?: string }).nodeId ?? ''
      return !jobIds.has(targetId)
    })
    const totalCount = uniqueLedgerExports.length + windowJobs.length

    // Top 5 most recent
    const jobRecords: LiveModuleRecord[] = windowJobs.map((job) => ({
      id: `job-${job.id}`,
      primary: `Export Job · ${job.status}`,
      secondary: `${job.creditsCharged} credits`,
      numeric: job.creditsCharged,
      drillTarget: 'SUMMARY',
    }))

    const ledgerList: LiveModuleRecord[] = uniqueLedgerExports.map((r) => {
      const actor = r.actorName?.trim() || r.actorId || 'Member'
      return {
        id: `export-${r.id}`,
        primary: `${actor} · ${r.action || 'CSV Export'}`,
        secondary: r.targetTitle?.trim() || 'Audit export',
        drillTarget: 'SUMMARY',
      }
    })

    const records = [...jobRecords, ...ledgerList].slice(0, 5)

    const headline: LiveModuleHeadline = {
      value: totalCount,
      delta,
      label: 'exports',
    }

    const sparkline: LiveModuleSparkline = {
      points,
      direction: 'up-is-good',
    }

    return {
      headline,
      sparkline,
      records,
      loading: auditLoading || jobsLoading,
      error: auditError || jobsError,
    }
  }, [auditRecords, jobs, timeRange, now, auditLoading, jobsLoading, auditError, jobsError])
}
